import { Component, type ErrorInfo, type ReactNode } from "react";

export function RouteLoading({ label = "Загружаем раздел…" }: { label?: string }) {
  return <section className="panel dashboard-loading" role="status" aria-live="polite" aria-busy="true">
    <span>{label}</span>
  </section>;
}

interface RouteLoadBoundaryProps {
  children: ReactNode;
  resetKey: string;
}

interface RouteLoadBoundaryState {
  failed: boolean;
}

/**
 * A route chunk can fail when a new immutable release replaces assets while a
 * tab is still open. Reloading preserves the hash deep link and asks the
 * browser for the current release manifest and chunk names.
 */
export class RouteLoadBoundary extends Component<RouteLoadBoundaryProps, RouteLoadBoundaryState> {
  state: RouteLoadBoundaryState = { failed: false };
  private errorState: HTMLElement | null = null;

  static getDerivedStateFromError(): RouteLoadBoundaryState {
    return { failed: true };
  }

  componentDidCatch(_error: unknown, _info: ErrorInfo) {
    window.requestAnimationFrame(() => this.errorState?.focus({ preventScroll: true }));
  }

  componentDidUpdate(previous: RouteLoadBoundaryProps) {
    if (previous.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  private retry = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.failed) return this.props.children;
    return <section
      ref={(element) => { this.errorState = element; }}
      className="panel registry"
      role="alert"
      tabIndex={-1}
      aria-labelledby="route-load-error-title"
    >
      <div className="empty-state">
        <h2 id="route-load-error-title">Не удалось открыть раздел</h2>
        <p>Интерфейс не загрузился. Проверьте соединение и повторите попытку.</p>
        <button className="primary-button" type="button" onClick={this.retry}>Повторить</button>
      </div>
    </section>;
  }
}
